import React from 'react';
import { useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import { VictoryBar, VictoryChart, VictoryAxis, VictoryLabel } from './Victory';
import { H5 } from '../Typography';

const StyledView = styled.View`
  background: white;
  border-radius: 24px;
  padding: 2px 0;
  align-items: center;
  width: 100%;
  max-width: 100%;
`;

const StyledTitle = styled(H5)`
  font-size: 16px;
  line-height: 18px;
  margin-bottom: 10px;
  text-align: center;
  font-family: 'WorkSans-SemiBold';
  letter-spacing: -0.65px;
`;

export type BarChartProps = {
  title?: string;
  data: Array<{
    x: string | number;
    y: number | null;
    color?: string;
    hasData?: boolean;
  }>;
  yAxisLabel?: string;
  yAxisLeftOffset?: number;
  height?: number;
  width?: number;
  showLegend?: boolean;
};

const BarChart: React.FC<BarChartProps> = ({
  title,
  data,
  yAxisLabel = 'Value',
  yAxisLeftOffset = 0,
  height = 200,
  width
}) => {
  const window = useWindowDimensions();
  const isTablet = window.width >= 740 && window.width <= 1024;
  const isDesktop = window.width >= 1025 && window.width <= 1439;
  const isLargeDesktop = window.width >= 1440;

  const chartWidth =
    width ||
    window.width *
      (isTablet && window.height <= 800
        ? 0.48
        : isTablet && window.width >= 900
        ? 0.47
        : isTablet
        ? 0.59
        : isDesktop
        ? 0.37
        : isLargeDesktop
        ? 0.2
        : 0.88);

  const maxValue = Math.max(...data.map(d => d.y || 0));
  const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10;

  const yTickValues = Array.from(
    { length: 6 },
    (_, i) => Math.round((yAxisMax / 5) * i * 10) / 10
  );

  return (
    <StyledView>
      {title && <StyledTitle color='dark'>{title}</StyledTitle>}
      <VictoryChart
        height={height}
        width={chartWidth}
        padding={{ top: 10, bottom: 50, left: 50, right: 30 }}
        domain={{ y: [0, yAxisMax] }}
        domainPadding={{ x: 100 }}
      >
        {/* Y-Axis */}
        <VictoryAxis
          crossAxis={false}
          label={yAxisLabel}
          dependentAxis
          offsetX={50}
          tickValues={yTickValues}
          tickFormat={(tickValue: number) => tickValue.toFixed(1)}
          fixLabelOverlap
          style={{
            axis: {
              stroke: '#D8D8DC',
              strokeWidth: 1.5
            },
            tickLabels: {
              fill: Colors.darkText,
              fontSize: '12px',
              fontFamily: 'WorkSans-Medium',
              lineHeight: '14px',
              letterSpacing: '-0.49px',
              padding: 8 - yAxisLeftOffset
            },
            axisLabel: {
              fill: Colors.darkText,
              fontSize: '12px',
              fontFamily: 'WorkSans-Medium',
              lineHeight: '12px',
              letterSpacing: '-0.49px'
            },
            grid: {
              stroke: Colors.lightText,
              strokeOpacity: 0.3,
              padding: 100
            }
          }}
        />

        {/* X-Axis */}
        <VictoryAxis
          offsetY={49.2}
          tickValues={data.map(d => d.x)}
          tickFormat={(tickValue: string | number) => tickValue.toString()}
          style={{
            axis: { stroke: '#D8D8DC', strokeWidth: 1.5 },
            tickLabels: {
              fill: Colors.darkText,
              fontSize: '12px',
              fontFamily: 'WorkSans-Medium',
              lineHeight: '14px',
              letterSpacing: '-0.49px'
            },
            axisLabel: {
              fill: Colors.darkText,
              fontSize: '12px',
              fontFamily: 'WorkSans-Medium',
              lineHeight: '12px',
              letterSpacing: '-0.49px'
            }
          }}
        />

        {/* Bars */}
        <VictoryBar
          data={data}
          style={{
            data: {
              fill: ({ datum }: { datum: any }) => datum.color || Colors.primary
            }
          }}
          barWidth={60}
          labels={({ datum }: { datum: any }) =>
            datum.hasData === false ? '-' : `${datum.y.toFixed(1)}`
          }
          labelComponent={
            <VictoryLabel
              dy={0}
              style={{
                fontSize: '20px',
                fill: Colors.darkText,
                fontFamily: 'WorkSans-Medium',
                letterSpacing: '-0.65px'
              }}
            />
          }
        />
      </VictoryChart>
    </StyledView>
  );
};

export default BarChart;
